<template>
  <div class="text-[0]">
    <Label
      v-if="label"
      :class="{
        'ml-3': async,
      }"
      :label-action="computedLabelAction"
      :required="required"
      @label-action-clicked="onLabelActionClicked"
    >
      {{ label }}
      <template #additional-icon>
        <slot name="additional-icon" />
      </template>
    </Label>
    <Combobox
      v-model="selectedOption"
      as="div"
      :class="selectComponentId"
      :nullable="clearable"
    >
      <div class="group relative">
        <Float flip portal adaptive-width show>
          <div>
            <span
              v-if="user || $slots.icon"
              class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
            >
              <slot name="icon">
                <UserAvatar
                  :user="
                    selectedOption?.[idKey as keyof typeof selectedOption] ===
                      unassignedOptionId
                      ? ({} as User)
                      : (selectedOption as User) || undefined
                  "
                  :has-popover="hasPopover"
                  popover-position="right"
                  size="6"
                />
              </slot>
            </span>

            <ComboboxInput
              ref="inputRef"
              class="w-full rounded-md border-0 bg-white py-1.5 pr-9 text-gray-900 ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:!ring-indigo-600 sm:text-sm sm:leading-6"
              :class="[
                selectComponentId,
                user || $slots.icon ? 'pl-11' : 'pl-3',
                isLoading ? 'cursor-progress' : '',
                async
                  ? 'ring-transparent focus:bg-gray-50 focus:shadow-sm'
                  : 'py-1.5 shadow-sm ring-gray-300',
                computedDisabled
                  ? async
                    ? ''
                    : 'cursor-not-allowed !bg-gray-50 !text-gray-500 !ring-gray-200'
                  : async
                    ? 'cursor-text group-hover:bg-gray-50 group-hover:ring-gray-300'
                    : 'cursor-text text-gray-900',
              ]"
              :placeholder="computedPlaceholder"
              :display-value="getDisplayValue"
              :disabled="computedDisabled || isLoading"
              autocomplete="off"
              @change="inputChanged($event.target.value)"
              @focus="inputFocused = true"
              @mousedown="inputFocused = true"
            />

            <button
              v-if="
                isLoading ||
                  ((!inputFocused || !computedClearable || !selectedOption) &&
                    !computedDisabled)
              "
              :id="`headlessui-combobox-custom-button-${selectComponentId}`"
              class="absolute inset-y-0 right-0 items-center rounded-r-md px-2 focus:outline-none"
              :class="[
                selectComponentId,
                async
                  ? inputFocused || isLoading
                    ? 'flex'
                    : 'hidden disabled:!hidden group-hover:flex'
                  : 'flex disabled:cursor-not-allowed',
                isLoading ? 'cursor-progress' : '',
              ]"
              @click="toggleSelection"
            >
              <kbd
                v-if="keyboardShortcut"
                class="hidden items-center rounded border border-gray-200 bg-white px-2 text-xs font-bold text-gray-400 shadow-sm sm:inline-flex"
              >
                {{ keyboardShortcut }}
              </kbd>
              <ChevronUpDownIcon
                v-if="!isLoading"
                class="size-5 text-gray-400"
                aria-hidden="true"
              />
              <Spinner v-else colored />
            </button>

            <button
              v-else-if="computedClearable && !computedDisabled"
              class="absolute inset-y-0 right-0 items-center rounded-r-md px-2 focus:outline-none"
              @click="clear"
            >
              <XMarkIcon class="size-5 text-gray-400" aria-hidden="true" />
            </button>
          </div>

          <!-- DO NOT REMOVE THIS EMPTY DIV - IT IS NEEDED BY FLOATING-UI -->
          <div>
            <ComboboxOptions
              v-if="inputFocused && !computedDisabled && !isLoading"
              :class="selectComponentId"
              class="z-10 my-1 max-h-60 w-full overflow-auto overflow-x-clip rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm"
              static
            >
              <div v-if="!!$slots.header" class="border-b border-gray-200">
                <slot name="header" :query="query" />
              </div>

              <div
                v-if="!filteredOptions.length"
                class="my-4 flex w-full items-center justify-center"
              >
                <Spinner v-if="isAutoCompleteSpinnerLoading" colored />
                <span v-else class="block px-2 text-center text-gray-500">
                  {{ $t('general.no-options-found') }}
                </span>
              </div>
              <template v-else>
                <ComboboxOption
                  v-for="(option, index) in filteredOptions"
                  :key="string ? option : option[idKey]"
                  v-slot="{ active, selected }"
                  :value="option"
                  :class="hasDivider(index) ? 'border-b border-gray-200' : ''"
                  as="template"
                >
                  <li
                    :class="[
                      'relative cursor-pointer select-none py-2 pl-3 pr-9',
                      active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                    ]"
                  >
                    <div class="flex items-center">
                      <UserAvatar
                        v-if="user"
                        :user="(option as User) || undefined"
                        size="6"
                        class="mr-2 shrink-0"
                        :has-popover="false"
                      />

                      <slot
                        v-else-if="$slots.prefix"
                        name="prefix"
                        :element="option"
                      />

                      <span :class="['truncate', selected && 'font-semibold']">
                        {{ string ? option : option[filterKey] }}
                        <span class="sr-only">
                          is {{ string ? option : option[filterKey] }}
                        </span>
                      </span>
                    </div>
                    <span
                      v-if="selected"
                      :class="[
                        'absolute inset-y-0 right-0 flex items-center pr-4',
                        active ? 'text-white' : 'text-indigo-600',
                      ]"
                    >
                      <CheckIcon class="size-5" aria-hidden="true" />
                    </span>
                  </li>
                </ComboboxOption>
              </template>
              <div
                v-if="!!$slots.footer"
                class="border-t-2 border-solid border-gray-300"
              >
                <slot name="footer" />
              </div>
            </ComboboxOptions>
          </div>
        </Float>
      </div>
    </Combobox>
  </div>
</template>

<script lang="ts" setup>
// DOCS: https://rollouter.atlassian.net/wiki/spaces/RCP/pages/7864426/Select
import { v4 as uuid } from 'uuid';
import {
  CheckIcon,
  ChevronUpDownIcon,
  XMarkIcon,
} from '@heroicons/vue/20/solid';
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/vue';
import type { Resolver } from '@trpc/client';
import { debounce, isEqual } from 'lodash';
import {
  type User,
  type DATABASE_AUTOCOMPLETE_COLLECTION,
  unassignedOptionId,
} from '@/types';
import { useUserStore } from '@/stores/user';
import { useOutsideClick } from '@/util/use-outside-click';

/* eslint-disable @typescript-eslint/no-explicit-any */

const props = defineProps({
  modelValue: {
    type: [Object, String],
  },
  options: {
    type: Array as PropType<any[]>,
    default: () => [] as [],
  },
  idKey: {
    type: String,
    default: '_id',
  },
  filterKey: {
    type: String,
    default: 'name',
  },
  string: {
    type: Boolean,
    default: false,
  },
  queryEndpoint: {
    type: Function as PropType<Resolver<any>>,
    default: undefined,
  },
  queryParams: {
    type: Object,
    default: undefined,
  },
  placeholder: {
    type: String,
    default: undefined,
  },
  label: {
    type: String,
    default: undefined,
  },
  labelAction: {
    type: String,
    default: undefined,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  disabledOptions: {
    type: Array as PropType<any[]>,
    default: () => [] as [],
  },
  async: {
    type: Boolean,
    default: false,
  },
  user: {
    type: Boolean,
    default: false,
  },
  showSelfAssign: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  divideAfterIndexes: {
    type: Array as PropType<number[]>,
    default: () => [],
  },
  clearable: {
    type: Boolean,
    default: true,
  },
  autoCompleteCollection: {
    type: String as PropType<DATABASE_AUTOCOMPLETE_COLLECTION>,
  },
  required: {
    type: Boolean,
  },
  hasPopover: {
    type: Boolean,
    default: true,
  },
  buttonId: {
    type: String,
  },
  keyboardShortcut: {
    type: String,
  },
});

const emit = defineEmits([
  'update:modelValue',
  'optionsFetched',
  'labelActionClicked',
]);

// Generate unique Component Id for each Select-Component initially
// This is needed to control click outside closing (see hideInput())
const selectComponentId = props.buttonId || uuid();

const computedClearable = computed(() => props.clearable && !props.required);

const { $client } = useNuxtApp();

// Query input
const inputRef = ref<typeof ComboboxInput>();
const inputFocused = ref(false);
const query = ref('');

const selectedOption = computed({
  get: () => props.modelValue ?? null,
  set: (newValue) => {
    if (isEqual(newValue, props.modelValue)) {
      return;
    }
    emit('update:modelValue', newValue);
  },
});

const fetchedOptions = ref([]);
const isFetchingOptions = ref(!!props.queryEndpoint);
const hasFetchedAutoCompleteInitially = ref(false);
const isAutoCompleteSpinnerLoading = computed(() => {
  if (!props.autoCompleteCollection) {
    return false;
  }

  if (!hasFetchedAutoCompleteInitially.value) {
    return true;
  }

  return (
    props.autoCompleteCollection &&
    !innerOptions.value?.length &&
    isAutoCompleteLoading.value
  );
});
const isAutoCompleteLoading = ref(false);

const innerOptions = computed(() => {
  return props.queryEndpoint || !!props.autoCompleteCollection
    ? fetchedOptions.value
    : props.options;
});

const toggleSelection = () => {
  if (!inputFocused.value) {
    focusInput();
  } else {
    inputFocused.value = false;
    query.value = '';
  }
};

watch(
  computed(() => props.modelValue),
  () => {
    if (props.modelValue !== null) {
      inputFocused.value = false;
    }
  }
);

const i18n = useI18n();

const isLoading = computed(() => isFetchingOptions.value || props.loading); // In case we need to activate the components loading state in the parent

const focusInput = () => {
  if (!props.disabled && !isLoading.value) {
    inputFocused.value = true;

    // nextTick doesn't work here
    setTimeout(() => inputRef.value?.$el.focus(), 0);
  }
};
const getDisplayValue = (option: any) => {
  if (!option) {
    return '';
  }
  return props.string ? option : option[props.filterKey];
};

const hideInput = (event: Event) => {
  let isChildOfCombobox = false;
  let currentNode = event.target;

  // Closing the combobox options before the v-model update was emitted cancels it
  while (currentNode !== null) {
    if (
      (currentNode as HTMLElement).id?.startsWith('headlessui-combobox') &&
      (currentNode as HTMLElement).classList.contains(selectComponentId)
    ) {
      isChildOfCombobox = true;
      break;
    }
    currentNode = (currentNode as HTMLElement).parentNode;
  }

  if (!isChildOfCombobox) {
    inputFocused.value = false;
    query.value = '';
  }
};

const inputChanged = (changedValue: string | null) => {
  if (changedValue === null) {
    query.value = '';
  } else {
    query.value = changedValue;
  }

  inputFocused.value = true;
};

useOutsideClick(
  [inputRef.value?.$el],
  hideInput,
  computed(() => inputFocused.value),
  { forceOverridePreventDefault: true }
);

// Computed properties
const filteredOptions = computed(() => {
  if (innerOptions.value.length) {
    let optionsToFilter = innerOptions.value;

    if (props.disabledOptions.length) {
      optionsToFilter = innerOptions.value.filter((option) => {
        const isSelected = props.string
          ? option === selectedOption.value
          : option[props.idKey] ===
            selectedOption.value?.[
              props.idKey as keyof typeof selectedOption.value
            ];
        const isDisabled = props.disabledOptions.find((find) =>
          props.string
            ? option === find
            : option[props.idKey] === find[props.idKey]
        );

        return isSelected || !isDisabled;
      });
    }

    return query.value.trim().length === 0
      ? optionsToFilter
      : optionsToFilter.filter((option) => {
        return (props.string ? option : option[props.filterKey])
          .toLowerCase()
          .includes(query.value.trim().toLowerCase());
      });
  }
  return [];
});

const computedDisabled = computed(
  () =>
    props.disabled ||
    isLoading.value ||
    (!filteredOptions.value.length &&
      !props.autoCompleteCollection &&
      props.disabledOptions.length === innerOptions.value.length)
);

const computedPlaceholder = computed(() => {
  if (isLoading.value) {
    return i18n.t('general.loading');
  }

  if (
    props.disabledOptions.length === innerOptions.value.length &&
    !props.autoCompleteCollection
  ) {
    return i18n.t('general.no-options-available');
  }

  return props.placeholder ?? i18n.t('general.none');
});

// Label action

const { user: currentUser } = useUserStore();

const computedLabelAction = computed(() => {
  if (props.user && props.showSelfAssign) {
    if ((selectedOption.value as User)?._id !== currentUser._id) {
      return i18n.t('general.assign-to-me');
    }
  }
  return props.labelAction;
});

const onLabelActionClicked = () => {
  if (!props.disabled) {
    emit('labelActionClicked');

    if (props.user && props.showSelfAssign) {
      selectedOption.value = currentUser;
    }
  }
};

// Util functions

const clear = () => {
  selectedOption.value = null;
};

const hasDivider = (index: number) => {
  return (
    filteredOptions.value.length > 1 && props.divideAfterIndexes.includes(index)
  );
};

const fetchAutoCompletion = debounce(
  (collection: DATABASE_AUTOCOMPLETE_COLLECTION) => {
    isAutoCompleteLoading.value = true;

    const payload = {
      collection,
      q: query.value.trim(),
      ...props.queryParams,
    };
    $client.search.autoComplete
      .query(payload)
      .then((res) => {
        fetchedOptions.value = res.items;

        if (!hasFetchedAutoCompleteInitially.value) {
          hasFetchedAutoCompleteInitially.value = true;
        }
      })
      .catch((err) => {
        useErrorHandler(err, {
          type: 'error',
          message: err,
        });
      })
      .finally(() => {
        isAutoCompleteLoading.value = false;
      });
  },
  300
);

// Fetching

if (props.queryEndpoint && !props.autoCompleteCollection) {
  props
    .queryEndpoint(props.queryParams)
    .then(({ items }) => {
      fetchedOptions.value = items;
      isFetchingOptions.value = false;
      emit('optionsFetched', items);
    })
    .catch((err) => {
      useErrorHandler(err, {
        type: 'error',
        message: err,
      });
    });
}

watch(
  computed(() => query.value),
  () => {
    if (props.autoCompleteCollection) {
      hasFetchedAutoCompleteInitially.value = false;
      fetchAutoCompletion(props.autoCompleteCollection);
    }
  }
);

watch(
  computed(() => inputFocused.value),
  (newValue) => {
    const hasFetchAutoComplete =
      props.autoCompleteCollection && !innerOptions.value?.length && newValue;
    if (hasFetchAutoComplete) {
      fetchAutoCompletion(props.autoCompleteCollection);
    }
  }
);
</script>
